import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { t } from '../../../../i18n';

const addAccreditationApi = new Api('accreditations');
const editAccreditationApi = new Api('accreditations/:id');

function* addNewAccreditation(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const accreditation = keyByProperty(propsArray, 'name', prop => prop.value);
    accreditation.country_id = accreditation.country;
    accreditation.accreditation_skills_attributes = editForm.positions.reduce((arr, item) => {
      arr.push(
        ...item.accreditation_skills.map(skill => {
          if (typeof skill === 'string') {
            return { skill_id: skill };
          }
          // eslint-disable-next-line no-underscore-dangle
          skill._destroy = skill._destroy || item._destroy;

          return skill;
        }),
      );

      return arr;
    }, []);
    delete accreditation.positions;

    const result = yield call(
      sendRequest,
      addAccreditationApi.createItem,
      {
        data: {
          accreditation,
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        const { id } = result.data;

        yield put(actions.saveSuccess());
        yield put(actions.loadData({ id }));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.added_successfully', {
              item: t('dash.admin.accreditations.new_accreditation_title'),
            }),
          }),
        );

        yield put(push(`/accreditations/${id}`));
      } else {
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateAccreditation(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;
    const propsArray = Object.values(editForm);
    const accreditation = keyByProperty(propsArray, 'name', prop => prop.value);

    const {
      example_document_image_url,
      icon,
      example_document_image,
      icon_url: _icon_url,
      ...otherProps
    } = accreditation;

    const saveAccreditationObject = {
      ...otherProps,
    };

    if (example_document_image_url && !example_document_image_url.startsWith('http')) {
      const imageObject = {
        icon,
        example_document_image,
      };
      saveAccreditationObject.imageObject = imageObject;
    }
    [
      { key: 'industries', newKey: 'industry_ids' },
      { key: 'states', newKey: 'state_ids' },
      { key: 'sectors', newKey: 'sector_ids' },
    ].forEach(item => {
      if (accreditation[item.key] && Array.isArray(accreditation[item.key])) {
        accreditation[item.newKey] = accreditation[item.key];
        delete accreditation[item.key];
      }

      return;
    });
    accreditation.country_id = accreditation.country;
    accreditation.accreditation_skills_attributes = editForm.positions.reduce((arr, item) => {
      arr.push(
        ...item.accreditation_skills.map(skill => {
          if (typeof skill === 'string') {
            return { skill_id: skill };
          }
          // eslint-disable-next-line no-underscore-dangle
          skill._destroy = skill._destroy || item._destroy;

          return skill;
        }),
      );

      return arr;
    }, []);
    delete accreditation.positions;
    const result = yield call(
      sendRequest,
      editAccreditationApi.updateItem,
      {
        data: {
          accreditation,
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        yield put(actions.saveSuccess());
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t('dash.admin.accreditations.accreditation_title'),
            }),
          }),
        );

        yield put(push(`/accreditations/${id}`));
      } else {
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormDataRequest({ payload }) {
  try {
    const result = yield call(
      sendRequest,
      new Api(`accreditations/form_data?country_id=${payload.countryId}`).load,
      {},
      notifications.REQUEST_ERROR,
    );

    if (result && result.data && result.data.success && result.data.success.data) {
      const { data } = result.data.success;
      yield put(actions.loadFormDataSuccess({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadData({ payload }) {
  try {
    const { id, isEdit } = payload;
    const result = yield call(sendRequest, editAccreditationApi.load, { id: payload.id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      const { data } = result.data.success;
      yield put(actions.loadDataSuccess({ data: { id: payload.id, ...data } }));

      if (isEdit) {
        yield put(actions.edit({ id, data: { ...data } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD_DATA, loadData),
    takeEvery(actions.ADD, addNewAccreditation),
    takeEvery(actions.SAVE, updateAccreditation),
    takeEvery(actions.LOAD_FORM_DATA, loadFormDataRequest),
  ]);
}
